import { Box, Column, Row, Spinner, Typography } from '@tempmee/react';
import { format } from 'date-fns';
import { FETCH_RECEIPTS_V2 } from '~/actions';
import { parseDateString } from '~/utils/DateUtils';
import { colors } from '@tempmee/tailwind';
import { DataGrid, LineClamp, ScrollArea, SortingArrow } from '~/commonComponents';
import browserHistory from '~/browserHistory';
import { useLoading } from '~/hooks';
import { currencyFormat } from '~/hooks/currencyFormat';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { IReceiptV2, ReceiptType } from '~/models';
import { Colors } from '~/themes/colors';
import { SortFields } from './enums';
import { useAllTransactionReceiptV2 } from './hooks';
import { ReceiptStatus } from './Status';
import { useRef } from 'react';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';

interface IProps {
  filters: {
    transactionDate: string;
  };
}

const getColors = (type: ReceiptType) => {
  switch (type) {
    case ReceiptType.ListingFee:
      return Colors.jobs_listing_fee_background;
    case ReceiptType.OnsiteInterview:
      return Colors.jobs_interview_background;
    default:
      return Colors.jobs_shift_background;
  }
};

const getText = (type: ReceiptType) => {
  switch (type) {
    case ReceiptType.ListingFee:
      return 'LISTING FEE';
    case ReceiptType.OnsiteInterview:
      return 'INTERVIEW';
    default:
      return 'SHIFT';
  }
};

// ListingFee receipts aren't tied to a shift (shiftDate is null), so the Posting/Shift Date
// column shows the moment the listing fee was charged — that's effectively when the posting
// was created. transactionDate is a real timestamp, so we parse it with new Date(). Shift
// and OnsiteInterview rows use shiftDate, a calendar-day value that needs the local-noon
// anchor from parseDateString to stay on the right day across timezones.
const getPostingOrShiftDate = (item: IReceiptV2): Date | null => {
  if (item.type === ReceiptType.ListingFee) {
    return new Date(item.transactionDate);
  }
  return item.shiftDate ? parseDateString(item.shiftDate) : null;
};

export const ReceiptTableV2 = ({ filters }: IProps) => {
  const { receipts, scrollContainerRef, sort, onFetchNextPage, onSortChange } =
    useAllTransactionReceiptV2({ filters });
  const { isLoading } = useLoading([FETCH_RECEIPTS_V2]);

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  return (
    <ScrollArea
      ref={scrollContainerRef}
      onEndReached={onFetchNextPage}
      style={{
        minHeight: 100,
      }}
    >
      <DataGrid
        columns={[
          {
            key: 'office',
            header: 'Office',
            render: (item) => (
              <Typography as="span" data-cy="office-name-column" style={{ paddingRight: 8 }}>
                <LineClamp lineClamp={2} text={item.officeName} />
              </Typography>
            ),
          },
          ...(isEnforcePermanentJobV2
            ? [
                {
                  key: 'type',
                  header: 'Type',
                  width: '140px',
                  render: (item: IReceiptV2) => (
                    <Box
                      style={{
                        alignSelf: 'flex-start',
                        padding: '2px 10px',
                        borderRadius: 8,
                        backgroundColor: getColors(item.type),
                      }}
                    >
                      <Typography as="span" color={colors.neutral[500]}>{getText(item.type)}</Typography>
                    </Box>
                  ),
                },
              ]
            : []),
          {
            key: 'transactionDate',
            header: (
              <Row gap={2}>
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Transaction Date
                </Typography>
                <SortingArrow
                  name={SortFields.TransactionDate}
                  sorting={{
                    direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                    field: sort.field,
                  }}
                  toggleOnly
                  onChange={(val) => onSortChange(val?.field as SortFields, val)}
                />
              </Row>
            ),
            width: '185px',
            render: (item) => (
              <Column data-cy="payment-date-column">
                <Typography as="span">{format(new Date(item.transactionDate), 'EEE, MMM dd, yyyy')}</Typography>
                <Typography as="span">{format(new Date(item.transactionDate), 'hh:mm a')}</Typography>
              </Column>
            ),
          },
          {
            key: 'shiftDate',
            header: (
              <Row gap={2}>
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  {isEnforcePermanentJobV2 ? 'Posting Date' : 'Shift Date'}
                </Typography>
                <SortingArrow
                  name={SortFields.ShiftDate}
                  sorting={{
                    direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                    field: sort.field,
                  }}
                  onChange={(val) => onSortChange(val?.field as SortFields, val)}
                />
              </Row>
            ),
            width: '185px',
            render: (item) => {
              const date = getPostingOrShiftDate(item);
              return (
                <Typography as="span" data-cy="shift-date-column" data-testid="shift-date-column">
                  {date ? format(date, 'EEE, MMM dd, yyyy') : ''}
                </Typography>
              );
            },
          },
          {
            key: 'total',
            header: 'Total',
            width: '120px',
            render: (item) => {
              const isRefund = item.totalRefund > 0;

              return (
                <Typography
                  as="span"
                  bold
                  color={isRefund ? colors.success[600] : colors.neutral[600]}
                  data-cy="payment-amount-column"
                  data-testid="payment-amount-column"
                  size="body_small"
                >
                  <span>{isRefund ? '-' : ''}$</span>
                  <span>
                    {isRefund ? currencyFormat(item.totalRefund) : currencyFormat(item.totalCharge)}
                  </span>
                </Typography>
              );
            },
          },
          {
            key: 'status',
            header: 'Status',
            width: '120px',
            render: (item) => (
              <Box data-cy="payment-status-column">
                <ReceiptStatus status={item.status} />
              </Box>
            ),
          },
          {
            key: 'action',
            header: '',
            width: '50px',
            align: 'center',
            render: () => <OutlineChevronRightIcon />,
          },
        ]}
        data={receipts}
        dataCy={{
          header: 'payment-history-table',
          root: 'all-transactions-table',
          rowItem: 'payment-history-table-row',
        }}
        dataTestId={{
          header: 'payment-history-table',
          root: 'all-transactions-table',
          rowItem: 'payment-history-table-row',
        }}
        keyExtractor={(it) => `${it.officeId}.${it.shiftId}.${it.transactionDate}.${it.status}`}
        isStickyHeader
        onRowClick={(it) => {
          if (isEnforcePermanentJobV2 && it.type === ReceiptType.ListingFee && it.postingId) {
            browserHistory.push(`/permanent-jobs/job-overview/${it.postingId}`);
            return;
          }

          if (isEnforcePermanentJobV2 && it.type === ReceiptType.ListingFee) {
            browserHistory.push(`/permanent-jobs/postings`);
            return;
          }

          browserHistory.push(`job/${it.shiftId}/details`, {
            job_id: it.shiftId,
            jobId: it.shiftId,
          });
        }}
      />
      {isLoading && (
        <Box style={{ marginTop: 24 }}>
          <Spinner centered variant="default" size="md" />
        </Box>
      )}
    </ScrollArea>
  );
};
